// Ausgangsrechnung von Hand erfassen (SAAS-MODUS-DESIGN §4a): Kaeufer (inkl. Leitweg-ID),
// Daten, Positionen mit Live-Summen. Ergebnis ist ein normaler Ausgangs-Entwurf — danach
// laeuft alles wie gehabt (Detail: Validieren -> XRechnung/ZUGFeRD erzeugen -> Download).
// Die Summen hier sind nur Vorschau; massgeblich rechnet der Server (computeTotals).

import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Card, SectionTitle, money, inputCls, btnPrimary, btnGhost } from './ui';
import { getSeller, createManualOutbound } from '../api';

const UNITS = ['C62', 'HUR', 'DAY', 'KGM', 'MTR', 'LS'];
const RATES = [19, 7, 0];
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const today = () => new Date().toISOString().slice(0, 10);
const newLine = () => ({ name: '', quantity: 1, unitCode: 'C62', netPrice: '', vatRate: 19 });

const OutboundManualForm = ({ user, isDarkMode, t, onCancel, onCreated, showSuccess, showError }) => {
  const [hasSeller, setHasSeller] = useState(null);
  const [buyer, setBuyer] = useState({ name: '', vatId: '', street: '', postCode: '', city: '', countryCode: 'DE', email: '' });
  const [head, setHead] = useState({ invoiceNumber: '', issueDate: today(), deliveryDate: '', dueDate: '', orderReference: '', buyerReference: '', paymentTerms: '', currency: 'EUR' });
  const [lines, setLines] = useState([newLine()]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getSeller(user).then((r) => setHasSeller(!!r.seller)).catch((e) => showError?.(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Intl wirft bei unvollstaendigem Code (waehrend der Eingabe) -> Vorschau in EUR.
  const cur = /^[A-Z]{3}$/i.test(head.currency) ? head.currency.toUpperCase() : 'EUR';
  const lineNet = (l) => round2(Number(l.quantity || 0) * Number(l.netPrice || 0));
  const net = round2(lines.reduce((s, l) => s + lineNet(l), 0));
  const tax = round2(RATES.reduce((s, rate) =>
    s + round2(lines.filter((l) => Number(l.vatRate) === rate).reduce((a, l) => a + lineNet(l), 0) * rate / 100), 0));

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  const submit = async () => {
    setBusy(true);
    try {
      const res = await createManualOutbound(user, {
        ...head, currency: head.currency.toUpperCase(),
        buyer: {
          name: buyer.name, vatId: buyer.vatId,
          address: { street: buyer.street, postCode: buyer.postCode, city: buyer.city, countryCode: buyer.countryCode.toUpperCase() },
          electronicAddress: buyer.email ? { scheme: 'EM', id: buyer.email } : undefined,
        },
        lines: lines.map((l) => ({ ...l, quantity: Number(l.quantity), netPrice: Number(l.netPrice), vatRate: Number(l.vatRate) })),
      });
      if (res.ok === false) { showError?.(res.error); return; }
      showSuccess?.(`${t('einvoicing.draftCreatedMsg')}: ${res.invoiceNumber}`);
      onCreated(res.invoiceId);
    } catch (e) { showError?.(e.message); }
    finally { setBusy(false); }
  };

  const labelCls = `block text-xs mb-1 ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`;
  const muted = isDarkMode ? 'text-white/45' : 'text-slate-500';
  const input = `${inputCls(isDarkMode)} w-full`;
  const grid = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3';
  const sub = `text-xs font-semibold mt-5 mb-2 ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`;
  const field = (obj, set, key, label, extra = {}) => (
    <label className="block min-w-0">
      <span className={labelCls}>{label}</span>
      <input className={input} value={obj[key]} onChange={(e) => set((o) => ({ ...o, [key]: e.target.value }))} {...extra} />
    </label>
  );

  return (
    <Card isDarkMode={isDarkMode}>
      <SectionTitle isDarkMode={isDarkMode}>{t('einvoicing.manual.title')}</SectionTitle>
      {hasSeller === false && (
        <p className={`text-sm mb-3 ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>{t('einvoicing.manual.noSeller')}</p>
      )}

      <h4 className={sub}>{t('einvoicing.manual.buyer')}</h4>
      <div className={grid}>
        {field(buyer, setBuyer, 'name', t('einvoicing.manual.buyerName'), { required: true })}
        {field(buyer, setBuyer, 'vatId', t('einvoicing.manual.vatId'))}
        {field(buyer, setBuyer, 'email', t('einvoicing.manual.eAddress'), { type: 'email', required: true })}
        {field(buyer, setBuyer, 'street', t('einvoicing.manual.street'))}
        {field(buyer, setBuyer, 'postCode', t('einvoicing.manual.postCode'), { required: true })}
        {field(buyer, setBuyer, 'city', t('einvoicing.manual.city'), { required: true })}
        {field(buyer, setBuyer, 'countryCode', t('einvoicing.manual.country'), { maxLength: 2, required: true })}
        {field(head, setHead, 'buyerReference', t('einvoicing.manual.leitwegId'), { required: true })}
      </div>

      <h4 className={sub}>{t('einvoicing.manual.dates')}</h4>
      <div className={grid}>
        {field(head, setHead, 'invoiceNumber', t('einvoicing.manual.invoiceNumber'), { placeholder: t('einvoicing.manual.invoiceNumberHint') })}
        {field(head, setHead, 'issueDate', t('einvoicing.manual.issueDate'), { type: 'date', required: true })}
        {field(head, setHead, 'deliveryDate', t('einvoicing.manual.deliveryDate'), { type: 'date' })}
        {field(head, setHead, 'dueDate', t('einvoicing.manual.dueDate'), { type: 'date', title: t('einvoicing.manual.dueOrTermsHint') })}
        {field(head, setHead, 'paymentTerms', t('einvoicing.manual.paymentTerms'), { title: t('einvoicing.manual.dueOrTermsHint') })}
        {field(head, setHead, 'orderReference', t('einvoicing.manual.orderReference'))}
        {field(head, setHead, 'currency', t('einvoicing.manual.currency'), { maxLength: 3 })}
      </div>

      <h4 className={sub}>{t('einvoicing.manual.lines')}</h4>
      <div className="space-y-3">
        {lines.map((l, i) => (
          <div key={i} className={`grid grid-cols-2 sm:grid-cols-12 gap-2 items-end rounded-xl p-3 border ${isDarkMode ? 'border-white/[0.12]' : 'border-slate-200'}`}>
            <label className="col-span-2 sm:col-span-3 min-w-0">
              <span className={labelCls}>{t('einvoicing.manual.lineName')}</span>
              <input className={input} value={l.name} onChange={(e) => setLine(i, 'name', e.target.value)} />
            </label>
            <label className="sm:col-span-1 min-w-0">
              <span className={labelCls}>{t('einvoicing.manual.quantity')}</span>
              <input type="number" step="any" className={input} value={l.quantity} onChange={(e) => setLine(i, 'quantity', e.target.value)} />
            </label>
            <label className="sm:col-span-2 min-w-0">
              <span className={labelCls}>{t('einvoicing.manual.unit')}</span>
              <select className={input} value={l.unitCode} onChange={(e) => setLine(i, 'unitCode', e.target.value)}>
                {UNITS.map((u) => <option key={u} value={u}>{t(`einvoicing.manual.unit.${u}`)}</option>)}
              </select>
            </label>
            <label className="sm:col-span-2 min-w-0">
              <span className={labelCls}>{t('einvoicing.manual.netPrice')}</span>
              <input type="number" step="0.01" className={input} value={l.netPrice} onChange={(e) => setLine(i, 'netPrice', e.target.value)} />
            </label>
            <label className="sm:col-span-2 min-w-0">
              <span className={labelCls}>{t('einvoicing.manual.vatRate')}</span>
              <select className={input} value={l.vatRate} onChange={(e) => setLine(i, 'vatRate', Number(e.target.value))}>
                {RATES.map((r) => <option key={r} value={r}>{r === 0 ? t('einvoicing.manual.rate0') : `${r} %`}</option>)}
              </select>
            </label>
            <div className="col-span-2 sm:col-span-2 flex items-center justify-between gap-2 min-w-0">
              <span className={`text-sm tabular-nums ${muted}`} title={t('einvoicing.manual.lineNet')}>{money(lineNet(l), cur)}</span>
              <button className={btnGhost(isDarkMode)} disabled={lines.length === 1} aria-label={t('einvoicing.manual.removeLine')}
                title={t('einvoicing.manual.removeLine')} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        <p className={`text-xs ${muted}`}>{t('einvoicing.manual.vatHint')}</p>
        <button className={btnGhost(isDarkMode)} onClick={() => setLines((ls) => [...ls, newLine()])}>
          <Plus className="w-4 h-4 mr-1.5" />{t('einvoicing.manual.addLine')}
        </button>
      </div>

      <div className={`mt-5 ml-auto max-w-xs text-sm space-y-1 tabular-nums ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}>
        <div className="flex justify-between"><span>{t('einvoicing.manual.totalNet')}</span><span>{money(net, cur)}</span></div>
        <div className="flex justify-between"><span>{t('einvoicing.manual.totalTax')}</span><span>{money(tax, cur)}</span></div>
        <div className="flex justify-between font-semibold"><span>{t('einvoicing.manual.totalGross')}</span><span>{money(round2(net + tax), cur)}</span></div>
      </div>

      <div className="flex gap-2 justify-end mt-5">
        <button className={btnGhost(isDarkMode)} onClick={onCancel}>{t('common.cancel')}</button>
        <button className={btnPrimary(isDarkMode)} disabled={busy || hasSeller === false} onClick={submit}>{t('einvoicing.manual.save')}</button>
      </div>
    </Card>
  );
};

export default OutboundManualForm;
